// Which first-run tour steps a given client should actually see.
//
// A step only appears if it will mean something to this client. A client
// invoiced for nothing yet should not be walked through invoices — an empty
// screen teaches them the portal is empty.
//
// Two steps are not gated on their own section having content. Messages is how
// they reach a human, so it is never irrelevant. Approvals is where the work
// they are paying for gets signed off: anyone with a project will be asked to
// approve something, and gating it on an approval that already exists meant
// nobody ever saw the step — they would meet their first approval request with
// no idea the section existed.

export const TOUR_STEPS = [
  {
    key: 'project',
    anchor: '[data-tour="project-card"]',
    fallbackAnchor: '[data-tour="nav-projects"]',
    title: 'Your project at a glance',
    body: "Phase, progress and what's next — updated as I work, so you never have to ask where things stand.",
  },
  {
    key: 'approvals',
    anchor: '[data-tour="nav-approvals"]',
    title: 'When I need you',
    body: 'Anything waiting on you shows up here. One click to approve or ask for changes.',
  },
  {
    key: 'files',
    anchor: '[data-tour="nav-files"]',
    title: 'Send me files, and find yours',
    body: 'Drop logos, photos or documents here rather than emailing them. Everything I deliver lands here too.',
  },
  {
    key: 'brand',
    anchor: '[data-tour="nav-brand-assets"]',
    title: 'Your brand kit',
    body: 'Finished logos, colours and fonts, ready to hand to anyone who needs them.',
  },
  {
    key: 'invoices',
    anchor: '[data-tour="nav-invoices"]',
    title: 'Invoices and payment',
    body: "What's due, what's paid, and a card link for the next payment on your plan.",
  },
  {
    key: 'requests',
    anchor: '[data-tour="nav-requests"]',
    title: 'Need something new?',
    body: "Ask for a new piece of work here — a campaign, a site, a one-off. It reaches me as a request, not a lost email.",
  },
  {
    key: 'messages',
    anchor: '[data-tour="nav-messages"]',
    title: 'Just ask',
    body: 'Messages come straight to me, not a support queue. Anything unclear, start here.',
  },
]

// Some steps teach an action, others show content, and they are gated
// differently. "Here is where you upload files" is worth saying to someone
// with no files — that is the entire point of a walkthrough. "Here are your
// invoices" is not worth saying to someone who has none, because it opens an
// empty screen and teaches them the portal is empty.
const ACTION_STEPS = new Set(['files', 'requests', 'messages'])

// `content` counts what the client actually has. Absent keys count as zero, so
// a caller that fails to load one section hides that step rather than
// promising a screen that turns out to be empty.
export function stepsForClient(content = {}) {
  const has = (k) => Number(content[k] || 0) > 0

  return TOUR_STEPS.filter((step) => {
    if (ACTION_STEPS.has(step.key)) return true

    switch (step.key) {
      case 'project': return has('projects')
      // Anyone with work in flight will be asked to approve something, so the
      // section is never irrelevant to them.
      case 'approvals': return has('approvals') || has('projects')
      case 'invoices': return has('invoices')
      case 'brand': return has('brandAssets')
      default: return false
    }
  })
}

// A tour runs on first sign-in only, and only if it has more than the one
// always-on step to show — a single card is a popup, not a walkthrough.
export function shouldAutoStart(client, content = {}) {
  if (!client) return false
  if (client.tour_completed_at || client.tour_skipped_at) return false
  return stepsForClient(content).length > 1
}
